import React, { useState } from 'react';
import { determineMTBCategory, calculateAgeInCurrentYear, getAllEligibleCategories } from '../utils/categoryUtils';

const CategoryCalculator: React.FC = () => {
  const [dateOfBirth, setDateOfBirth] = useState<string>('');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [category, setCategory] = useState<any>(null);
  const [ageThisYear, setAgeThisYear] = useState<number | null>(null);
  const [eligibleCategories, setEligibleCategories] = useState<any[]>([]);
  
  const handleCalculate = () => {
    if (!dateOfBirth) return;
    
    const dob = new Date(dateOfBirth);
    const calculatedAge = calculateAgeInCurrentYear(dob);
    const calculatedCategory = determineMTBCategory(dob, gender);
    const allEligibleCategories = getAllEligibleCategories(dob, gender);
    
    setAgeThisYear(calculatedAge);
    setCategory(calculatedCategory);
    setEligibleCategories(allEligibleCategories);
  };
  
  const currentYear = new Date().getFullYear();

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-lg mx-auto">
      <h2 className="text-xl font-bold mb-4">MTB Category Calculator</h2>
      <p className="text-gray-600 mb-4">
        This calculator determines a rider's MTB category based on their date of birth and gender,
        following the rule that categories are based on the age a rider will turn during {currentYear}.
      </p>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
        <input
          type="date"
          className="w-full p-2 border rounded-md"
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
        <select
          className="w-full p-2 border rounded-md"
          value={gender}
          onChange={(e) => setGender(e.target.value as 'male' | 'female')}
        >
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
      </div>
      
      <button
        className="w-full bg-primary text-white py-2 rounded-md hover:bg-primary-dark mb-4"
        onClick={handleCalculate}
        disabled={!dateOfBirth}
      >
        Calculate Category
      </button>
      
      {ageThisYear !== null && (
        <div className="mt-4 p-4 bg-gray-50 rounded-md">
          <p className="mb-2">
            <span className="font-medium">Age in {currentYear}:</span> {ageThisYear} years
          </p>
          
          {category ? (
            <div className="mb-2">
              <p className="font-medium">Primary Racing Category:</p>
              <div className="mt-2 p-3 border rounded-md bg-orange-50">
                <p className="font-bold text-primary">{category.category} ({category.code})</p>
                <p className="text-sm text-gray-600">Age range: {category.minAge}-{category.maxAge === 200 ? '+' : category.maxAge} years</p>
              </div>
            </div>
          ) : (
            <p className="text-red-500">No matching category found.</p>
          )}
          
          {eligibleCategories.length > 1 && (
            <div className="mt-4">
              <p className="font-medium">All Eligible Categories:</p>
              <div className="mt-2 space-y-2">
                {eligibleCategories.map((cat, index) => (
                  <div key={index} className="p-3 border rounded-md">
                    <p className="font-bold">{cat.category} ({cat.code})</p>
                    <p className="text-sm text-gray-600">Age range: {cat.minAge}-{cat.maxAge === 200 ? '+' : cat.maxAge} years</p>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Note: Riders aged 23+ may be eligible for both age-specific and Elite categories.
              </p>
            </div>
          )}
          
          <div className="mt-4 text-sm text-gray-500">
            <p>
              <strong>How this works:</strong> According to UCI rules, a rider's category is determined by the age they will turn during the current calendar year ({currentYear}), regardless of when their actual birthday falls.
            </p>
            <p className="mt-1">
              For example, if a rider turns 8 on December 1st, {currentYear}, they will still be placed in the Nipper category for races in January {currentYear}, even though they are only 7 years old at that time.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryCalculator;
